/**
 * Search / Register 後の明細 state 組み立て（React 非依存・単一 SO#）。
 * Gen1TrunkingOrderRepeaterSerials と orderSearchResult を、ルールに従って合成・更新する。
 *
 * 構成:
 * - 突合: API 結果・登録済み行の (Model, Serial) 集合
 * - Search 成功後: 他 SO# Search ADD 除去 + 検索結果とのマスタ突合（filterRepeaterLinesAfterSearchSuccess）
 * - モーダル: orderSearchResult 行の組み立て・selected 同期
 * - Register: 旧 Search ADD を除去して行を詰め、残り ADD の後に選択行を連結
 * - 入口: buildSearchSuccessRepeaterPatch（Search 成功時の一括パッチ）
 */
import {
    filterNonAddSectionRepeaterLines,
    isAddSectionRepeaterLine,
    isSearchAddRepeaterLine,
    makeModelSerialKey,
    orderNumbersEqual,
    trimRepeaterField,
} from '../gen1RepeaterLineHelpers';

/** @typedef {{ model: string, serialNumber: string, orderNumber?: string, selected: boolean }} OrderSearchResultRow */

/** API 取得行から (Model, Serial) キー集合を作る（Search 結果との突合用）。 */
export function buildFetchedModelSerialSet(fetchedRows) {
    return new Set(
        (fetchedRows ?? []).map((r) =>
            makeModelSerialKey(
                r.BaseModel ?? r.model,
                r.SerialNumber ?? r.serialNumber
            )
        )
    );
}

/** 指定 SO# で登録済みの Search ADD 行から (Model, Serial) キー集合を作る。 */
export function buildRegisteredSearchAddModelSerialSet(lines, orderNumber) {
    return new Set(
        (lines ?? [])
            .filter(isSearchAddRepeaterLine)
            .filter((l) => orderNumbersEqual(l.OrderNumber, orderNumber))
            .map((l) => makeModelSerialKey(l.BaseModel, l.SerialNumber))
    );
}

/**
 * Search 成功後の明細行フィルタ（単一 SO#）。
 * - Search ADD 以外は維持
 * - 操作 SO# 以外の Search ADD は除去
 * - 同一 SO# の Search ADD は検索結果に存在する行のみ維持
 */
export function filterRepeaterLinesAfterSearchSuccess(
    lines,
    orderNumber,
    fetchedRows
) {
    const fetchedSet = buildFetchedModelSerialSet(fetchedRows);
    const opOrder = trimRepeaterField(orderNumber);

    return (lines ?? []).filter((line) => {
        if (!isSearchAddRepeaterLine(line)) {
            return true;
        }
        const lineOrderNo = trimRepeaterField(line.OrderNumber);
        if (!lineOrderNo || !orderNumbersEqual(lineOrderNo, opOrder)) {
            return false;
        }
        return fetchedSet.has(
            makeModelSerialKey(line.BaseModel, line.SerialNumber)
        );
    });
}

/** ADD セクション行数（手動 Add + Search ADD。Exchange ペア内 ADD は含まない）。 */
export function countAddSectionRepeaterLines(lines) {
    return (lines ?? []).filter(isAddSectionRepeaterLine).length;
}

/**
 * API 結果を Search モーダル行に変換する。
 * 登録済み Search ADD があれば selected を同期、なければ全行 selected: true。
 */
export function buildOrderSearchResultRows(
    fetchedRows,
    remainingLines,
    orderNumber
) {
    const registeredSet = buildRegisteredSearchAddModelSerialSet(
        remainingLines,
        orderNumber
    );
    const hasRegistered = registeredSet.size > 0;

    return (fetchedRows ?? []).map((r) => {
        const model = r.BaseModel ?? r.model ?? '';
        const serialNumber = r.SerialNumber ?? r.serialNumber ?? '';
        const key = makeModelSerialKey(model, serialNumber);
        return {
            model,
            serialNumber,
            orderNumber: r.OrderNumber ?? r.orderNumber ?? orderNumber,
            selected: hasRegistered ? registeredSet.has(key) : true,
        };
    });
}

/** モーダル再表示時、明細の登録状態に合わせて orderSearchResult の selected を同期する。 */
export function syncOrderSearchResultSelection(
    orderSearchResult,
    lines,
    orderNumber
) {
    const registeredSet = buildRegisteredSearchAddModelSerialSet(
        lines,
        orderNumber
    );

    return (orderSearchResult ?? []).map((row) => ({
        ...row,
        selected: registeredSet.has(
            makeModelSerialKey(row.model, row.serialNumber)
        ),
    }));
}

/** Search 由来 ADD 行をすべて除去する（手動 Add / Exchange / Remove は残す）。 */
export function stripSearchAddRepeaterLines(lines) {
    return (lines ?? []).filter((line) => !isSearchAddRepeaterLine(line));
}

/** 明細行の LineSequenceNumber の最大値（欠損・非数は無視）。 */
export function maxLineSequenceNumber(lines) {
    return (lines ?? []).reduce((acc, line) => {
        const value = Number(line?.LineSequenceNumber);
        return Number.isFinite(value) && value > acc ? value : acc;
    }, 0);
}

/** Register 選択行から Search 由来 ADD 明細行を組み立てる（LineSequenceNumber は呼び出し側で採番開始）。 */
export function buildSearchAddLinesFromSelection(
    selectedItems,
    orderNumber,
    startSequence
) {
    let nextSeq = startSequence;
    return (selectedItems ?? []).map((item) => ({
        LineSequenceNumber: nextSeq++,
        ExchangePairId: null,
        ExchangeSystemId: '',
        OperationType: 'ADD',
        OrderNumber: item.orderNumber ?? orderNumber,
        BaseModel: item.model ?? '',
        SerialNumber: item.serialNumber ?? '',
    }));
}

/**
 * Register 後の明細を組み立てる（単一 SO#）。
 * 1. 既存 Search ADD をすべて除去（行がなくなり、残り行が前に詰まる）
 * 2. 残った ADD セクション行は **相対順を維持**（手動/Search の明示的な並べ替えはしない）
 * 3. 選択行を新 Search ADD として残り ADD の直後に連結
 * 4. Exchange / Remove は ADD ブロック後に維持（Add → Exchange → Remove のブロック順）
 */
export function mergeRepeaterLinesAfterRegister(
    prevLines,
    orderNumber,
    selectedItems
) {
    const withoutSearchAdd = stripSearchAddRepeaterLines(prevLines);
    const remainingAddSectionLines = withoutSearchAdd.filter((line) =>
        isAddSectionRepeaterLine(line)
    );
    const afterAddSection = filterNonAddSectionRepeaterLines(withoutSearchAdd);
    // useEffect 再採番前の一時採番。残り行の既存 LineSequenceNumber と重複しないよう最大値+1 から振る（最終的な連番は Gen1Application の useEffect 側）
    const startSeq = maxLineSequenceNumber(withoutSearchAdd) + 1;
    const newAddLines = buildSearchAddLinesFromSelection(
        selectedItems,
        orderNumber,
        startSeq
    );
    const lines = [
        ...remainingAddSectionLines,
        ...newAddLines,
        ...afterAddSection,
    ];
    return {
        lines,
        repeaterQty: remainingAddSectionLines.length + newAddLines.length,
    };
}

/**
 * Search 成功時に orchestrator / hook へ渡す一括パッチ。
 * remainingLines・repeaterQty・orderSearchResult・hasResults を返す。
 */
export function buildSearchSuccessRepeaterPatch({
    prevLines,
    orderNumber,
    fetchedRows,
}) {
    const remainingLines = filterRepeaterLinesAfterSearchSuccess(
        prevLines,
        orderNumber,
        fetchedRows
    );
    const hasResults = (fetchedRows ?? []).length > 0;

    return {
        remainingLines,
        repeaterQty: countAddSectionRepeaterLines(remainingLines),
        orderSearchResult: buildOrderSearchResultRows(
            fetchedRows,
            remainingLines,
            orderNumber
        ),
        hasResults,
    };
}
